import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV2, NavEntryPoint } from '@retailer-platform/dashboard/routing'
import { catalogAdminAdminRouter } from './routing/catalogAdminAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { catalogAdminQualityAuditAccessConfiguration } from './access-control/catalogAdminQualityAuditAccess.configuration'
import { catalogAdminScopedRouter } from './routing/catalogAdminScopedRouter'
import { catalogAdminCatalogImageUploadsAccessConfiguration } from './access-control/catalogAdminCatalogImageUploadsAccess.configuration'
import { catalogAdminMDPlatformAccessConfiguration } from './access-control/catalogMDPlatformAccess.configuration'
import { catalogAdminExtractionModelsAccessConfiguration } from './access-control/catalogAdminExtractionModelsAccess.configuration'
import { catalogAdminProductEditAccessConfiguration } from './access-control/catalogAdminProductEditAccess.configuration.ts'
import { catalogAdminDataBridgeAccessConfiguration } from './access-control/catalogAdminDataBridgeAccess.configuration'
import { catalogAdminPriceAnomalyTakedownAccessConfiguration } from './access-control/catalogAdminPriceAnomalyTakedownAccess.configuration'
import { catalogAdminMapleJobDashboardAccessConfiguration } from './access-control/catalogAdminMapleJobDashboardAccess.configuration'
import { catalogAdminSourcePriorityAccessConfiguration } from './access-control/catalogAdminSourcePriorityAccess.configuration'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'catalog-admin',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'catalog-admin-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
  },
  routers: {
    admin: [catalogAdminAdminRouter],
    scoped: [catalogAdminScopedRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Catalog,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Upload and manage product catalog images',
          route: 'app-admin-catalog-admin-image-uploads',
          labelId: 'catalogAdminDomain.imageUploads.title',
          accessControl: catalogAdminCatalogImageUploadsAccessConfiguration,
          positionNavV2: 2,
          inToolPermissions: [],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.CatalogAdmin,
      subNavItems: [
        {
          route: 'app-admin-catalog-admin-quality-audit-workflow-list',
          subRoutes: [
            'app-admin-catalog-admin-quality-audit-workflow-batch-list',
            'app-admin-catalog-admin-quality-audit-workflow-task-list',
          ],
          labelId: 'catalogAdminDomain.qualityAuditWorkflows.nav.title',
          accessControl: catalogAdminQualityAuditAccessConfiguration,
          positionNavV2: 1,
        },
        {
          route: 'app-admin-catalog-admin-product-edit-show',
          labelId: 'catalogAdminDomain.productEdit.nav.title',
          accessControl: catalogAdminProductEditAccessConfiguration,
          positionNavV2: 0,
        },
        {
          route: 'app-admin-catalog-admin-md-platform',
          labelId: 'catalogAdminDomain.mdPlatform.nav.title',
          accessControl: catalogAdminMDPlatformAccessConfiguration,
          positionNavV2: 2,
        },
        {
          route: 'app-admin-catalog-admin-attribute-management',
          labelId: 'catalogAdminDomain.attributeManagement.nav.title',
          accessControl: catalogAdminExtractionModelsAccessConfiguration,
          positionNavV2: 3,
        },
        {
          route: 'app-admin-catalog-admin-price-anomaly-takedown',
          labelId: 'catalogAdminDomain.priceAnomalyInvestigation.nav.title',
          accessControl: catalogAdminPriceAnomalyTakedownAccessConfiguration,
          positionNavV2: 4,
        },
        {
          route: 'app-admin-catalog-admin-data-bridge',
          subRoutes: [
            'app-admin-catalog-admin-data-bridge-batch-list',
            'app-admin-catalog-admin-data-bridge-create',
            'app-admin-catalog-admin-data-bridge-edit',
            'app-admin-catalog-admin-md-platform-details',
          ],
          labelId: 'catalogAdminDomain.dataBridge.nav.title',
          accessControl: catalogAdminDataBridgeAccessConfiguration,
          positionNavV2: 5,
        },
        {
          route: 'app-admin-catalog-admin-maple-job-dashboard',
          subRoutes: ['app-admin-catalog-admin-maple-job-details'],
          labelId: 'catalogAdminDomain.mapleJobDashboard.nav.title',
          accessControl: catalogAdminMapleJobDashboardAccessConfiguration,
          positionNavV2: 6,
        },
        {
          route: 'app-admin-catalog-admin-source-priority',
          subRoutes: [
            'app-admin-catalog-admin-source-priority-create',
            'app-admin-catalog-admin-source-priority-edit',
            'app-admin-catalog-admin-source-priority-pending-queue',
            'app-admin-catalog-admin-source-priority-impact-analysis',
          ],
          labelId: 'catalogAdminDomain.sourcePriority.nav.title',
          accessControl: catalogAdminSourcePriorityAccessConfiguration,
          positionNavV2: 7,
        },
      ],
    },
  ],
}

export default definition
